import React, { useEffect, useState, useCallback } from 'react';
import { getActivityLogs, exportActivityLogs } from '../services/api';
import './ActivityLogs.css';

const ACTION_OPTIONS = [
  'user_created', 'user_deleted', 'login', 'login_failed', 'grant_access',
  'revoke_access', 'access_request_created', 'access_request_approved',
  'access_request_rejected', 'repository_creation_requested', 'repo_created',
  'repo_access_granted', 'repo_access_revoked',
  'bulk_operation', 'sync_collaborators', 'temporary_access_granted'
];

const ActivityLogs = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    action: '',
    startDate: '',
    endDate: ''
  });
  const [page, setPage] = useState(0);
  const limit = 50;

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = { limit, skip: page * limit, ...filters };
      Object.keys(params).forEach((k) => !params[k] && delete params[k]);
      const res = await getActivityLogs(params);
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Error fetching activity logs:', err);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleExport = async () => {
    try {
      const params = { ...filters };
      Object.keys(params).forEach((k) => !params[k] && delete params[k]);
      const res = await exportActivityLogs(params);
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'activity-logs.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  return (
    <div className="activity-logs-page">
      <div className="activity-logs-header">
        <div>
          <h1 className="activity-logs-title">Activity Logs</h1>
          <p className="activity-logs-desc">Audit trail of admin and security events ({total} total)</p>
        </div>
        <button className="btn btn-secondary" onClick={handleExport}>Export CSV</button>
      </div>

      <div className="activity-logs-filters card">
        <select
          className="form-select"
          value={filters.action}
          onChange={(e) => { setPage(0); setFilters({ ...filters, action: e.target.value }); }}
        >
          <option value="">All actions</option>
          {ACTION_OPTIONS.map((a) => (
            <option key={a} value={a}>{a.replace(/_/g, ' ')}</option>
          ))}
        </select>
        <input
          type="date"
          className="form-input"
          value={filters.startDate}
          onChange={(e) => { setPage(0); setFilters({ ...filters, startDate: e.target.value }); }}
        />
        <input
          type="date"
          className="form-input"
          value={filters.endDate}
          onChange={(e) => { setPage(0); setFilters({ ...filters, endDate: e.target.value }); }}
        />
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => { setPage(0); setFilters({ action: '', startDate: '', endDate: '' }); }}
        >
          Clear
        </button>
      </div>

      {loading ? (
        <div className="spinner" />
      ) : (
        <div className="activity-logs-table card">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Action</th>
                <th>By</th>
                <th>Target</th>
                <th>IP</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr><td colSpan={6} className="empty-row">No activity logs found</td></tr>
              ) : logs.map((log) => (
                <tr key={log._id}>
                  <td>{log.createdAt ? new Date(log.createdAt).toLocaleString() : '—'}</td>
                  <td><span className="action-badge">{log.action?.replace(/_/g, ' ')}</span></td>
                  <td>{log.adminUsername || log.adminId?.username || '—'}</td>
                  <td>{log.targetUsername || log.targetUserId?.username || '—'}</td>
                  <td className="ip-cell">{log.ipAddress || '—'}</td>
                  <td className="details-cell">{log.details ? JSON.stringify(log.details).slice(0, 80) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="activity-logs-pagination">
        <button className="btn btn-secondary btn-sm" disabled={page === 0} onClick={() => setPage(page - 1)}>Previous</button>
        <span>Page {page + 1} of {Math.max(1, Math.ceil(total / limit))}</span>
        <button className="btn btn-secondary btn-sm" disabled={(page + 1) * limit >= total} onClick={() => setPage(page + 1)}>Next</button>
      </div>
    </div>
  );
};

export default ActivityLogs;
